<template>
  <v-card class="px-2">
    <v-card-title>
      {{ isAdd ? "Add Member" : "Profile Details" }}
      <template v-if="close">
        <v-spacer></v-spacer>
        <v-btn icon @click="$emit('close')">
          <v-icon>close</v-icon>
        </v-btn>
      </template>
    </v-card-title>
    <v-card-subtitle>
      {{
        isAdd
          ? "Fill out the following form to add a member"
          : "Change profile info at any time"
      }}
    </v-card-subtitle>
    <v-card-text class="pb-0">
      <v-form ref="form" v-model="valid">
        <v-row :dense="isAdd">
          <v-col cols="12" sm="6" :md="md">
            <v-text-field
              v-model="user.firstName"
              label="First name"
              maxlength="50"
              :rules="[$rules.required]"
              required
              outlined
              :disabled="!allowSave"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" :md="md">
            <v-text-field
              v-model="user.lastName"
              label="Last name"
              maxlength="50"
              :rules="[$rules.required]"
              required
              outlined
              :disabled="!allowSave"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" :md="md">
            <v-text-field
              v-model="user.email"
              label="Email"
              maxlength="100"
              :rules="[$rules.required, $rules.email]"
              required
              outlined
              :disabled="!allowSave"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" :md="md">
            <v-text-field
              v-model="user.phoneNumber"
              label="Phone"
              :rules="[$rules.phone]"
              required
              outlined
              :disabled="!allowSave"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" :md="md">
            <v-text-field
              v-model="user.jobTitle"
              label="Job title"
              maxlength="50"
              :rules="[$rules.required]"
              required
              outlined
              :disabled="!allowSave"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" :md="md">
            <v-menu
              v-model="hiredate_menu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
              :disabled="!allowSave"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formattedDate(user.hireDate)"
                  outlined
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  label="Hire Date"
                  @click:clear="user.hireDate = null"
                  append-icon="today"
                  @click:append="hiredate_menu = true"
                  :disabled="!allowSave"
                >
                </v-text-field>
              </template>
              <v-date-picker
                v-model="user.hireDate"
                @input="hiredate_menu = false"
                :max="maxDate"
                :disabled="!allowSave"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" :md="md">
            <team-autocomplete
              v-model="team"
              :multiple="false"
              label="Department"
              :disabled="
                !allowSave || settings.planType == $enums.companyPlanTypes.LITE
              "
            >
            </team-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" :md="md">
            <member-autocomplete
              v-model="manager"
              :multiple="false"
              label="Direct Manager"
              :disabled="
                !allowSave || settings.planType == $enums.companyPlanTypes.LITE
              "
              :directManagerId="user.id"
              allowAll
            >
            </member-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" :md="md">
            <v-select
              label="Role"
              v-model="user.systemRoleId"
              :items="filtered_roles"
              item-text="name"
              item-value="id"
              outlined
              :rules="[$rules.required]"
              required
              :disabled="!allowSave"
            >
            </v-select>
          </v-col>
          <v-col cols="12" sm="6" :md="md" v-if="showCoreCoach">
            <v-select
              label="Is CoreCoach?"
              v-model="user.isCoreCoach"
              :items="yesNoItems"
              item-text="name"
              item-value="id"
              outlined
              :rules="[$rules.required]"
              required
              :disabled="!allowSave"
            >
            </v-select>
          </v-col>
          <v-col cols="12" sm="6" :md="md" v-if="!isAdd">
            <v-select
              label="Is Paused?"
              v-model="user.isPaused"
              :items="yesNoItems"
              item-text="name"
              item-value="id"
              outlined
              :disabled="!allowSave"
            >
            </v-select>
          </v-col>
          <v-col cols="12" sm="6" :md="md">
            <v-text-field
              v-model="user.location"
              label="Location"
              maxlength="100"
              outlined
              :disabled="!allowSave"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" :md="md">
            <v-select
              label="Level"
              v-model="user.levelId"
              :items="levels"
              item-text="name"
              item-value="id"
              outlined
              :disabled="!allowSave"
            >
            </v-select>
          </v-col>
          <v-col
            cols="12"
            :sm="
              field.customField.typeId == $enums.customFieldTypes.LARGE_TEXT
                ? 12
                : 6
            "
            :md="
              field.customField.typeId == $enums.customFieldTypes.LARGE_TEXT
                ? 12
                : md
            "
            v-for="(field, i) in user.customFields"
            :key="i"
          >
            <v-text-field
              v-model="field.value"
              outlined
              :label="field.customField.name"
              :required="field.customField.isRequired"
              :rules="field.customField.isRequired ? [$rules.required] : []"
              v-if="field.customField.typeId == $enums.customFieldTypes.TEXT"
              :disabled="!allowSave"
            ></v-text-field>
            <v-menu
              v-model="field.menu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
              :disabled="!allowSave"
              v-else-if="
                field.customField.typeId == $enums.customFieldTypes.DATE
              "
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formattedDate(field.value)"
                  outlined
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  :label="field.customField.name"
                  @click:clear="field.value = null"
                  append-icon="today"
                  @click:append="field.menu = true"
                  :disabled="!allowSave"
                  :rules="field.customField.isRequired ? [$rules.required] : []"
                >
                </v-text-field>
              </template>
              <v-date-picker
                v-model="field.value"
                @input="field.menu = false"
                :disabled="!allowSave"
              ></v-date-picker>
            </v-menu>
            <v-textarea
              v-else-if="
                field.customField.typeId == $enums.customFieldTypes.LARGE_TEXT
              "
              outlined
              v-model="field.value"
              :label="field.customField.name"
              :rules="field.customField.isRequired ? [$rules.required] : []"
            >
            </v-textarea>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
    <v-card-actions v-if="allowSave">
      <v-btn @click="save" :loading="saving">
        {{ isAdd ? "Add Member" : "Update" }}
      </v-btn>
      <v-btn
        @click="deleteUser"
        x-large
        color="secondarytext"
        text
        :loading="deleting"
        v-if="!isAdd"
      >
        Deactivate
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";
import MemberAutocomplete from "@/components/members/MemberAutocomplete";
import TeamAutocomplete from "@/components/teams/TeamAutocomplete";
export default {
  name: "MemberDetailForm",
  components: {
    MemberAutocomplete,
    TeamAutocomplete
  },
  props: {
    showCoreCoach: { type: Boolean, default: false },
    close: { type: Boolean, default: false }
  },
  data: () => ({
    valid: true,
    saving: false,
    deleting: false,
    hiredate_menu: false,
    yesNoItems: [
      { id: false, name: "No" },
      { id: true, name: "Yes" }
    ]
  }),
  watch: {
    user(val, old) {
      if (this._.isEmpty(old)) {
        this.$refs.form.resetValidation();
      }
    }
  },
  computed: {
    md() {
      return this.isAdd ? 6 : 4;
    },
    maxDate() {
      return moment().format("YYYY-MM-DD");
    },
    formattedDate() {
      return date => {
        if (date) {
          return moment(date).format("MM/DD/YYYY");
        }
        return "";
      };
    },
    manager: {
      get() {
        return [this.user.directManagerId];
      },
      set(val) {
        this.user.directManagerId = (val || [""])[0];
      }
    },
    team: {
      get() {
        return [this.user.teamId];
      },
      set(val) {
        this.user.teamId = (val || [""])[0];
      }
    },
    allowSave() {
      return this.is_admin;
    },
    filtered_roles() {
      if (this.is_manager) {
        return this.roles.filter(
          x => x.id != this.$enums.systemRoleTypes.MEMBER
        );
      } else {
        return this.roles.filter(
          x => x.id != this.$enums.systemRoleTypes.MANAGER
        );
      }
    },
    isAdd() {
      return !this.user.id;
    },
    ...mapGetters({
      user: "user/edit_user",
      levels: "lookup/levels",
      roles: "lookup/app_system_roles",
      is_manager: "user/is_manager",
      profile: "profile/profile",
      is_admin: "profile/is_admin",
      settings: "profile/settings",
      custom_fields: "company/custom_fields"
    })
  },
  created() {
    this.user.customFields = this.user.customFields || [];

    let currentIds = this.user.customFields.map(x => x.customField.id);

    let fieldsToAdd = this.custom_fields.filter(
      x => !currentIds.includes(x.id)
    );

    let self = this;
    this.user.customFields = this._.sortBy(
      this.user.customFields.concat(
        fieldsToAdd.map(x => ({ value: "", customField: x }))
      ),
      function(o) {
        return o.customField.typeId == self.$enums.customFieldTypes.LARGE_TEXT;
      }
    );
  },
  methods: {
    save() {
      if (this.$refs.form.validate()) {
        this.saving = true;

        this.$store
          .dispatch(this.isAdd ? "user/addUser" : "user/updateUser", this.user)
          .then(() => {
            this.$emit("save");
            this.$root.$snackbarSuccess("Member saved.");
          })
          .catch(error => {
            this.logError(error);
            this.$root.$alert(
              "Error Occurred",
              "An error occurred while updating this member."
            );
          })
          .finally(() => {
            this.saving = false;
          });
      }
    },
    async deleteUser() {
      if (
        await this.$root.$confirm(
          "Deactivate member",
          "Are you sure you want to deactivate this member?",
          { color: "red" }
        )
      ) {
        this.deleting = true;
        this.$store
          .dispatch("user/deleteUser", this.user.id)
          .then(() => {
            this.$emit("delete");
            this.$root.$snackbarSuccess("Member deactivated.");
          })
          .catch(error => {
            this.logError(error);
            this.$root.$alert(
              "Error Occurred",
              "An error occured while deleting this member."
            );
          })
          .finally(() => {
            this.deleting = false;
          });
      }
    }
  }
};
</script>
